import { Form, Input, Table, TimePicker } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import deleteSvg from "../../../../../../assets/svg/delete-red.svg";
import svg80 from "../../../../../../assets/svg/svg80.svg";
import svg88 from "../../../../../../assets/svg/svg88.svg";
import addSvg from "../../../../../../assets/svg/add-dark.svg";
import useModalToggle from "../../../../../../custom_hooks/useModalToggle";
import BlockDrawer from "./BlockDrawer";
import { setSelectedCandidates } from "../../../../../../features/folder/folderSlice";

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode =
    inputType === "time" ? (
      <TimePicker format="HH:mm" className="w-full input" id="input-focus" />
    ) : (
      <Input
        id="input-focus"
        className="input"
        placeholder={`Enter ${title}`}
      />
    );

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          className="table-form-item"
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `${title} is required`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export default function Block() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { open, handleCancel, handleOpen } = useModalToggle();
  const [prodd, setProdd] = useState();
  const showDrawer = async (item) => {
    await setProdd(item);
    await handleOpen();
  };
  const onClose = () => {
    handleCancel();
  };
  const { rowId, selectedCandidates } = useSelector((state) => state.folder);

  const [dataSource, setDataSource] = useState([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editingKey, setEditingKey] = useState("");

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const isEditing = (record) => record?.mbiId === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      ...record,
      mbiStartTime: record.mbiStartTime
        ? dayjs(record.mbiStartTime, "HH:mm")
        : null,
      mbiEndTime: record.mbiEndTime ? dayjs(record.mbiEndTime, "HH:mm") : null,
    });
    setEditingKey(record?.mbiId);
  };

  const cancel = async(record) => { 
  const isRowEmpty = !record?.mbiName && !record?.mbiStartTime && !record?.mbiEndTime;

  if (isRowEmpty) {
    setDataSource(prev => prev?.filter(item => item?.mbiId !== record?.mbiId));
  }
  
 await setEditingKey("");
};


  const saveFunction = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...dataSource];
      const index = newData.findIndex((item) => key === item?.mbiId);

      if (index > -1) {
        const item = newData[index];

        // Format time values
        const updatedObj = {
          ...item,
          ...row,
        };

        if (updatedObj.mbiStartTime && dayjs.isDayjs(updatedObj.mbiStartTime)) {
          updatedObj.mbiStartTime = updatedObj.mbiStartTime.format("HH:mm");
        }
        if (updatedObj.mbiEndTime && dayjs.isDayjs(updatedObj.mbiEndTime)) {
          updatedObj.mbiEndTime = updatedObj.mbiEndTime.format("HH:mm");
        }

        newData.splice(index, 1, updatedObj);
        setDataSource(newData);
        setEditingKey(""); 
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleAdd = () => {
    const lastRow = dataSource[dataSource.length - 1];
    if (
      lastRow &&
      (!lastRow.mbiName ||
        !lastRow.mbiStartTime ||
        !lastRow.mbiEndTime ||
        editingKey === lastRow.mbiId)
    ) {
      return;
    }

    const newId = Date.now();
    const newRow = {
      mbiId: newId,
      mbiName: "",
      mbiStartTime: null,
      mbiEndTime: null,
    };

    setDataSource([...dataSource, newRow]);
    setEditingKey(newId);

    // Set form values for the new row
    setTimeout(() => {
      form.setFieldsValue({
        mbiName: "",
        mbiStartTime: null,
        mbiEndTime: null,
      });
    }, 100);
  };

  const handleRemove = () => {
    const updatedData = dataSource.filter(
      (item) => !selectedRowKeys.includes(item.mbiId)
    );
    setDataSource(updatedData);
    setSelectedRowKeys([]);
  };

  const handleDeleteRow = async (record) => {
    const updatedData = dataSource?.filter(
      (item) => item?.mbiId !== record?.mbiId
    );
    setDataSource(updatedData);

    const filteredCandidates = selectedCandidates?.filter(
      (candidate) => candidate?.japInterviewSession !== record?.mbiName
    );
    await dispatch(setSelectedCandidates(filteredCandidates));
    if (editingKey === record?.mbiId) {
      setEditingKey("");
    }
  };

  const defaultColumns = [
    {
      title: "#",
      dataIndex: "index",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Session Name",
      dataIndex: "mbiName",
      editable: true,
    },
    {
      title: "Start Time",
      dataIndex: "mbiStartTime",
      editable: true,
    },
    {
      title: "Candidates",
      dataIndex: "candidates",
      render: (_, record) => {
        const count = selectedCandidates?.filter(
          (item) => item?.japInterviewSession === record?.mbiName
        )?.length;

        return count;
      },
    },

    {
      title: "End Time",
      dataIndex: "mbiEndTime",
      editable: true,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => {
        const editable = isEditing(record);

        const candidateCount =
          selectedCandidates?.filter(
            (item) => item?.japInterviewSession === record?.mbiName
          )?.length || 0;

        return editable ? (
          <div className="flex w-fit items-center gap-[1rem]">
            <button onClick={()=>cancel(record)} className="modal_btn_light" type="button">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => saveFunction(record?.mbiId)}
              className="modal_btn_dark"
            >
              Save
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            {candidateCount === 0 && (
              <img
                src={svg80}
                alt="svg80"
                className="cursor-pointer"
                onClick={() => edit(record)}
              />
            )}

            <img
              src={svg88}
              alt="svg88"
              className="cursor-pointer"
              onClick={() => showDrawer(record)}
            />

            <img
              src={deleteSvg}
              alt="deleteSvg"
              className="cursor-pointer"
              onClick={() => handleDeleteRow(record)}
            />
          </div>
        );
      },
    },
  ];

  const mergedColumns = defaultColumns?.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        inputType:
          col.dataIndex === "mbiStartTime" || col.dataIndex === "mbiEndTime"
            ? "time"
            : "text",
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <>
      <div className="grid grid-cols-1 ">
        <div className="max-w-full w-full overflow-x-auto">
          <Form form={form} component={false}>
            <Table
              className="editable_table"
              size="large"
              bordered={false}
              rowSelection={rowSelection}
              columns={mergedColumns}
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              dataSource={dataSource}
              rowKey="mbiId"
              pagination={{
                defaultPageSize: 10,
                hideOnSinglePage: true,
                pageSizeOptions: [10, 20, 50, 100],
              }}
              footer={() => (
                <div className="flex items-center w-full justify-between gap-[1.25rem] p-[1rem]">
                  {selectedRowKeys.length > 0 ? (
                    <button
                      type="button"
                      onClick={handleRemove}
                      className="flex items-center gap-[.5rem]"
                    >
                      <img src={deleteSvg} alt="deleteSvg" />

                      <span className="role_btn_text !text-[#C72C3B]">
                        Remove selected
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAdd}
                      disabled={dataSource.some(
                        (item) =>
                          !item.mbiName ||
                          !item.mbiStartTime ||
                          !item.mbiEndTime
                      )}
                      className="flex items-center gap-[.5rem]"
                    >
                      <div className="w-[2.5rem] h-[2.5rem] border border-[#0B363D] rounded-[.6875rem] flex items-center justify-center">
                        <img src={addSvg} alt="addSvg" />
                      </div>
                      <span className="text-[#0E0E0E] text-[.875rem] font-[400] font-dmSans">
                        Add
                      </span>
                    </button>
                  )}
                </div>
              )}
            />
          </Form>
        </div>
      </div>

      <BlockDrawer
        onClose={onClose}
        openDrawer={open}
        prodd={prodd}
        action={false}
      />
    </>
  );
}
